// Edit Lead Contact — drawer panel (lead theme)

import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  IconButton,
  CircularProgress,
  Autocomplete,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useLeadDetails } from '../../../contexts/LeadDetailsContext';
import { useModal } from '../../../contexts/ModalContext';

const T = {
  green: '#0a6b3e',
  greenLt: '#e5f5ec',
  greenMd: '#0d8a50',
  greenSf: 'rgba(10,107,62,0.06)',
  ink: '#0f1a13',
  subtle: '#526b5c',
  muted: '#8eaa98',
  bg: '#f5f8f6',
  border: '#dce8e1',
  borderL: '#eaf1ec',
  white: '#ffffff',
  red: '#dc2626',
  font: "'Inter','DM Sans','Plus Jakarta Sans',sans-serif",
};

const outlineLegendNoGap = {
  '& .MuiOutlinedInput-notchedOutline legend': {
    maxWidth: '0 !important',
    width: '0 !important',
    fontSize: 0,
    lineHeight: 0,
    padding: 0,
  },
  '& .MuiOutlinedInput-notchedOutline legend span': {
    padding: '0 !important',
    width: '0 !important',
    minWidth: '0 !important',
    display: 'block',
  },
};

const fieldSx = {
  ...outlineLegendNoGap,
  '& .MuiOutlinedInput-root': {
    fontFamily: T.font,
    fontSize: 13,
    borderRadius: 2,
    bgcolor: T.white,
    minHeight: 40,
    '& fieldset': { borderColor: T.borderL },
    '&:hover fieldset': { borderColor: T.border },
    '&.Mui-focused fieldset': { borderColor: T.green },
    '&.Mui-error fieldset': { borderColor: T.red },
  },
  '& .MuiInputLabel-root': {
    fontFamily: T.font,
    fontSize: 13,
    color: T.subtle,
    '&.Mui-focused': { color: T.green },
    '&.Mui-error': { color: T.red },
  },
  '& .MuiFormHelperText-root': {
    fontFamily: T.font,
    fontSize: 11.5,
    mt: 0.5,
    color: T.muted,
  },
  '& .MuiFormHelperText-root.Mui-error': { color: T.red },
  '& .MuiOutlinedInput-input::placeholder': { fontFamily: T.font, opacity: 0.55 },
};

const selectFormSx = {
  ...outlineLegendNoGap,
  '& .MuiOutlinedInput-root': {
    fontFamily: T.font,
    fontSize: 13,
    borderRadius: 2,
    bgcolor: T.white,
    minHeight: 40,
    '& fieldset': { borderColor: T.borderL },
    '&:hover fieldset': { borderColor: T.border },
    '&.Mui-focused fieldset': { borderColor: T.green },
  },
  '& .MuiInputLabel-root': {
    fontFamily: T.font,
    fontSize: 13,
    color: T.subtle,
    '&.Mui-focused': { color: T.green },
  },
};

const COUNTRY_CODES = [
  { value: '+91', label: '+91 (India)' },
  { value: '+1', label: '+1 (USA)' },
  { value: '+44', label: '+44 (UK)' },
  { value: '+61', label: '+61 (Australia)' },
  { value: '+65', label: '+65 (Singapore)' },
];

const EditLeadContact = React.memo(({ open, onClose, lead, source, onComplete }) => {
  const { loading, updateLeadDetails } = useLeadDetails();
  const { openSuccessModal, openErrorModal } = useModal();

  const [formData, setFormData] = useState({
    name: lead.contact_info?.name || '',
    email: lead.contact_info?.email || '',
    country_code: lead.contact_info?.country_code || '+91',
    mobile: lead.contact_info?.mobile || '',
    source: lead.source || lead.contact_info?.source || 'manual',
  });

  const [errors, setErrors] = useState({});
  const [sourceOptions, setSourceOptions] = useState([]);

  useEffect(() => {
    if (source && Array.isArray(source)) {
      setSourceOptions(source);
    } else if (source && Object.keys(source).length > 0) {
      const sourceOpts = Object.entries(source).map(([value, label]) => ({
        value,
        label,
      }));
      setSourceOptions(sourceOpts);
    }
  }, [source]);

  useEffect(() => {
    if (lead && open) {
      setFormData({
        name: lead.contact_info?.name || '',
        email: lead.contact_info?.email || '',
        country_code: lead.contact_info?.country_code || '+91',
        mobile: lead.contact_info?.mobile || '',
        source: lead.source || lead.contact_info?.source || 'manual',
      });
      setErrors({});
    }
  }, [lead, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'mobile') {
      const digitsOnly = value.replace(/\D/g, '');
      const limitedValue = digitsOnly.slice(0, 10);
      setFormData((prev) => ({
        ...prev,
        [name]: limitedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const isValidSource = (value) => {
    if (value === 'manual') return true;

    const isExistingSource = sourceOptions.some(
      (option) => option.value === value || option.label === value
    );

    if (!isExistingSource) {
      return (
        value.trim().length >= 3 && /^[a-zA-Z0-9\s.,&-]+$/.test(value)
      );
    }

    return true;
  };

  const handleAutocompleteChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'source') {
      if (!value || !value.trim()) {
        setErrors((prev) => ({
          ...prev,
          source: 'Source is required',
        }));
      } else if (!isValidSource(value)) {
        setErrors((prev) => ({
          ...prev,
          source:
            'Enter a valid source (min 3 characters, only letters, numbers, spaces, and basic punctuation)',
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          source: '',
        }));
      }
    } else if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email && !formData.mobile) {
      newErrors.email = 'Either email or mobile is required';
      newErrors.mobile = 'Either email or mobile is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (formData.mobile) {
      if (!/^\d{10}$/.test(formData.mobile)) {
        newErrors.mobile = 'Mobile number should be 10 digits';
      }
    }

    if (!formData.source || !formData.source.trim()) {
      newErrors.source = 'Source is required';
    } else if (!isValidSource(formData.source)) {
      newErrors.source =
        'Enter a valid source (min 3 characters, only letters, numbers, spaces, and basic punctuation)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const leadData = {
        name: formData.name.trim(),
        email: formData.email || '',
        country_code: formData.country_code,
        mobile: formData.mobile || '',
        source: formData.source,
      };

      const updatedData = await updateLeadDetails(lead.id, leadData);

      if (updatedData) {
        const sourceVerbose =
          updatedData.source_verbose ||
          updatedData.customer?.source_verbose ||
          (() => {
            const src =
              updatedData.customer?.source || updatedData.source || lead.source;
            if (src) {
              return src.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
            }
            return lead.source_verbose;
          })();

        const contactInfo = {
          name: `${updatedData.customer?.first_name || ''} ${updatedData.customer?.last_name || ''}`.trim(),
          email: updatedData.customer?.email || '',
          mobile: updatedData.customer?.mobile || '',
          country_code: updatedData.customer?.country_code || '+91',
          source: updatedData.customer?.source || updatedData.source || lead.source,
          source_verbose: sourceVerbose,
        };

        const updatedLead = {
          ...lead,
          contact_info: contactInfo,
          source: updatedData.customer?.source || updatedData.source || lead.source,
          source_verbose: sourceVerbose,
          updated_at:
            updatedData.customer?.updated_at || updatedData.updated_at || lead.updated_at,
          customer: updatedData.customer || lead.customer,
        };

        if (onComplete) {
          onComplete(updatedLead);
        }
      }

      openSuccessModal('Lead contact details updated successfully!', handleClose);
    } catch (error) {
      console.error('Error updating lead:', error);
      if (error.response && error.response.data) {
        const apiErrors = {};
        Object.entries(error.response.data).forEach(([key, value]) => {
          apiErrors[key] = Array.isArray(value) ? value.join(', ') : value;
        });
        setErrors(apiErrors);
      } else {
        openErrorModal('Failed to update lead contact details. Please try again.');
      }
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      country_code: '+91',
      mobile: '',
      source: 'manual',
    });
    setErrors({});
    onClose();
  };

  const autocompleteFieldSx = [
    fieldSx,
    {
      '& .MuiOutlinedInput-root': { minHeight: 40 },
      '& .MuiAutocomplete-input': { fontFamily: T.font, fontSize: 13 },
      '& .Mui-error .MuiOutlinedInput-notchedOutline': {
        borderColor: `${T.red} !important`,
      },
    },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 440 },
          maxWidth: '100vw',
          borderLeft: `1px solid ${T.borderL}`,
          boxShadow: '-12px 0 40px rgba(15,26,19,0.1), -2px 0 8px rgba(15,26,19,0.06)',
          bgcolor: T.white,
          fontFamily: T.font,
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                bgcolor: T.greenLt,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <PersonOutlineIcon sx={{ fontSize: 22, color: T.green }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="h2"
                sx={{
                  fontFamily: T.font,
                  fontSize: 17,
                  fontWeight: 700,
                  color: T.ink,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.3,
                }}
              >
                Edit lead contact
              </Typography>
              <Typography
                sx={{
                  fontFamily: T.font,
                  fontSize: 13,
                  color: T.subtle,
                  mt: 0.25,
                  lineHeight: 1.45,
                }}
              >
                Name, email or phone, and lead source
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            aria-label="Close"
            sx={{
              color: T.muted,
              mt: 0.25,
              flexShrink: 0,
              borderRadius: 2,
              '&:hover': { color: T.ink, bgcolor: T.greenSf },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Divider sx={{ borderColor: T.borderL }} />

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 2.5,
            py: 2,
            bgcolor: T.white,
          }}
        >
          <Grid container spacing={2.25}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="name"
                label="Name"
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name || 'Required'}
                required
                size="small"
                variant="outlined"
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                name="email"
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email || 'Use email or mobile below'}
                size="small"
                variant="outlined"
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={4}>
              <FormControl fullWidth size="small" sx={selectFormSx}>
                <InputLabel id="edit-lead-country-label">Code</InputLabel>
                <Select
                  name="country_code"
                  labelId="edit-lead-country-label"
                  value={formData.country_code}
                  onChange={handleChange}
                  label="Code"
                >
                  {COUNTRY_CODES.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      sx={{ fontFamily: T.font, fontSize: 13 }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={8}>
              <TextField
                fullWidth
                name="mobile"
                label="Mobile"
                value={formData.mobile}
                onChange={handleChange}
                error={!!errors.mobile}
                helperText={errors.mobile || '10 digits · use with email above'}
                size="small"
                variant="outlined"
                inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                options={sourceOptions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    const matchingOption = sourceOptions.find((opt) => opt.value === option);
                    if (matchingOption) return matchingOption.label;
                    return option === 'manual' ? 'Manual Entry' : option;
                  }
                  return option.value === 'manual' ? 'Manual Entry' : option.label;
                }}
                isOptionEqualToValue={(option, value) => {
                  if (typeof value === 'string') {
                    return option.value === value || option.label === value;
                  }
                  return option.value === value.value;
                }}
                value={formData.source}
                onChange={(event, newValue) => {
                  const sourceValue =
                    typeof newValue === 'string'
                      ? newValue
                      : newValue
                        ? newValue.value
                        : 'manual';
                  handleAutocompleteChange('source', sourceValue);
                }}
                onInputChange={(event, newInputValue) => {
                  if (event) {
                    handleAutocompleteChange('source', newInputValue || 'manual');
                  }
                }}
                freeSolo
                slotProps={{
                  paper: {
                    sx: {
                      borderRadius: 2,
                      border: `1px solid ${T.borderL}`,
                      fontFamily: T.font,
                      mt: 0.5,
                      boxShadow: '0 10px 28px rgba(15,26,19,0.1)',
                    },
                  },
                  listbox: {
                    sx: {
                      py: 0.5,
                      maxHeight: 280,
                      '& li': {
                        fontFamily: T.font,
                        fontSize: 13,
                        minHeight: 40,
                      },
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Source"
                    size="small"
                    variant="outlined"
                    placeholder="Select or type a source"
                    error={!!errors.source}
                    helperText={errors.source || 'Required'}
                    required
                    InputProps={{ ...params.InputProps, notched: false }}
                    sx={autocompleteFieldSx}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Typography sx={{ fontFamily: T.font, fontSize: 13 }}>
                      {typeof option === 'string'
                        ? option
                        : option.value === 'manual'
                          ? 'Manual Entry'
                          : option.label}
                    </Typography>
                  </li>
                )}
                sx={{
                  '& .MuiOutlinedInput-root': { minHeight: 40 },
                  '& .MuiAutocomplete-input': { fontFamily: T.font, fontSize: 13 },
                  '& .Mui-error .MuiOutlinedInput-notchedOutline': {
                    borderColor: `${T.red} !important`,
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            borderTop: `1px solid ${T.borderL}`,
            bgcolor: T.bg,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Button
            onClick={handleClose}
            size="small"
            sx={{
              fontFamily: T.font,
              fontSize: 13,
              fontWeight: 500,
              color: T.subtle,
              textTransform: 'none',
              borderRadius: 2,
              px: 2,
              '&:hover': { bgcolor: T.greenSf, color: T.ink },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading.details}
            size="small"
            sx={{
              fontFamily: T.font,
              fontSize: 13,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              px: 2.5,
              minWidth: 112,
              height: 36,
              bgcolor: T.green,
              color: '#fff',
              boxShadow: 'none',
              '&:hover': { bgcolor: T.greenMd, boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: T.borderL, color: T.muted },
            }}
          >
            {loading.details ? (
              <CircularProgress size={20} thickness={4} sx={{ color: '#fff' }} />
            ) : (
              'Update'
            )}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
});

export default EditLeadContact;
